import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { EmailExistsModal } from '@/components/ui/EmailExistsModal';
import { AnimatedBackdrop } from '@/components/AnimatedBackdrop';
import { Briefcase, TrendingUp, ArrowRight, User, Building2, Mail, Globe, DollarSign, Target, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

type UserRole = 'founder' | 'investor' | null;
type OnboardingStep = 'role' | 'profile' | 'details' | 'complete';

interface FounderProfile {
  name: string;
  email: string;
  companyName: string;
  website: string;
  description: string;
  stage: string;
  fundingNeeded: string;
  industry: string[];
  location: string;
}

interface InvestorProfile {
  name: string;
  email: string;
  firmName: string;
  website: string;
  investorType: string;
  checkSize: string;
  industries: string[];
  stages: string[];
  location: string;
}

const STAGES = ['Idea', 'MVP', 'Pre-seed', 'Seed', 'Series A', 'Series B+'];
const INVESTOR_TYPES = ['Angel Investor', 'Micro-VC', 'Venture Capital', 'Family Office', 'Corporate VC'];
const CHECK_SIZES = ['$5K-$25K', '$25K-$100K', '$100K-$500K', '$500K-$2M', '$2M+'];
const INDUSTRIES = [
  'AI/ML', 'SaaS', 'Fintech', 'Healthcare', 'Biotech', 'Consumer', 
  'Enterprise', 'Climate', 'Crypto/Web3', 'Hardware', 'Marketplace', 'Other'
];

export function OnboardingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, login, register, token: sessionToken } = useAuth();
  const [step, setStep] = useState<OnboardingStep>('role');
  const [role, setRole] = useState<UserRole>(null);
  const [showSignup, setShowSignup] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [showEmailExistsModal, setShowEmailExistsModal] = useState(false);
  const [existingEmail, setExistingEmail] = useState('');
  
  const [founderProfile, setFounderProfile] = useState<FounderProfile>({
    name: '',
    email: '',
    companyName: '',
    website: '',
    description: '',
    stage: '',
    fundingNeeded: '',
    industry: [],
    location: ''
  });
  
  const [investorProfile, setInvestorProfile] = useState<InvestorProfile>({
    name: '',
    email: '',
    firmName: '',
    website: '',
    investorType: '',
    checkSize: '',
    industries: [],
    stages: [],
    location: ''
  });

  const handleRoleSelect = (selectedRole: UserRole) => {
    setRole(selectedRole);
    setStep('profile');
  };

  const saveProfileToBackend = async (profileData: any, userRole: string, authToken?: string) => {
    try {
      const token = authToken || sessionToken;
      if (!token) return;

      const API_URL = import.meta.env.VITE_API_URL || '';
      
      // Prepare data based on role
      const payload = {
        role: userRole,
        ...(userRole === 'founder' ? {
          company_name: profileData.companyName,
          website: profileData.website,
          location: profileData.location,
          stage: profileData.stage,
          funding_amount: profileData.fundingNeeded,
          industries: JSON.stringify(profileData.industry || []),
          business_description: profileData.description,
        } : {
          firm_name: profileData.firmName,
          website: profileData.website,
          location: profileData.location,
          investor_type: profileData.investorType,
          check_size: profileData.checkSize,
          industries: JSON.stringify(profileData.industries || []),
          stages: JSON.stringify(profileData.stages || []),
        })
      };
      
      const res = await fetch(`${API_URL}/api/user/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        console.error('Failed to save profile:', await res.text());
      }
    } catch (e) {
      console.error('Failed to save profile:', e);
    }
  };

  const handleFounderSubmit = async () => {
    localStorage.setItem('backr_founder_profile', JSON.stringify(founderProfile));
    localStorage.setItem('backr_user_role', 'founder');
    
    if (isAuthenticated) {
      await saveProfileToBackend(founderProfile, 'founder');
    } else {
      // Pre-fill signup with collected data
      setSignupName(founderProfile.name);
      setSignupEmail(founderProfile.email);
    }
    setStep('complete');
  };

  const handleInvestorSubmit = async () => {
    localStorage.setItem('backr_investor_profile', JSON.stringify(investorProfile));
    localStorage.setItem('backr_user_role', 'investor');
    
    if (isAuthenticated) {
      await saveProfileToBackend(investorProfile, 'investor');
    } else {
      // Pre-fill signup with collected data
      setSignupName(investorProfile.name);
      setSignupEmail(investorProfile.email);
    }
    setStep('complete');
  };

  const handleCreateAccount = async () => {
    setIsCreatingAccount(true);
    try {
      const registerResult = await register(signupEmail, signupPassword, signupName);

      if (registerResult.success) {
        const profile = role === 'founder' ? founderProfile : investorProfile;
        // Context token may not have updated yet this soon after register();
        // read the fresh session directly.
        const { data: { session } } = await supabase.auth.getSession();
        await saveProfileToBackend(profile, role || 'founder', session?.access_token);

        // Signed in automatically even if they do not buy a plan yet.
        navigate('/pricing');
      } else {
        // If email already exists, auto-login and redirect appropriately
        if (registerResult.error?.toLowerCase().includes('already exists')) {
          setExistingEmail(signupEmail);
          setShowEmailExistsModal(true);
          
          // Try to login with the provided credentials using AuthContext
          console.log('Attempting auto-login for:', signupEmail);
          try {
            const loginResult = await login(signupEmail, signupPassword);
            console.log('Auto-login result:', loginResult);
            
            if (loginResult.success) {
              console.log('Auto-login successful, redirecting to pricing in 2s');
              // Login successful - wait a moment for modal to show, then redirect
              setTimeout(() => {
                navigate('/pricing');
              }, 2000);
              return;
            } else {
              console.error('Auto-login failed:', loginResult.error);
              // Show error in modal
              setExistingEmail(signupEmail + ' (Login failed: ' + (loginResult.error || 'Wrong password') + ')');
            }
          } catch (loginError) {
            console.error('Login threw exception:', loginError);
            setExistingEmail(signupEmail + ' (Login error - check console)');
          }
          return;
        }

        alert(registerResult.error || 'Failed to create account');
      }
    } catch (e) {
      console.error('Account creation exception:', e);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const toggleIndustry = (industry: string, isInvestor: boolean) => {
    if (isInvestor) {
      setInvestorProfile(prev => ({
        ...prev,
        industries: prev.industries.includes(industry)
          ? prev.industries.filter(i => i !== industry)
          : [...prev.industries, industry]
      }));
    } else {
      setFounderProfile(prev => ({
        ...prev,
        industry: prev.industry.includes(industry)
          ? prev.industry.filter(i => i !== industry)
          : [...prev.industry, industry]
      }));
    }
  };

  const toggleStage = (stage: string) => {
    setInvestorProfile(prev => ({
      ...prev,
      stages: prev.stages.includes(stage)
        ? prev.stages.filter(s => s !== stage)
        : [...prev.stages, stage]
    }));
  };

  return (
    <>
      <AnimatedBackdrop />
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 relative overflow-x-hidden">
      <div className="w-full max-w-2xl">
        <AnimatePresence mode="wait">
          {step === 'role' && (
            <motion.div
              key="role"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center px-1 sm:px-0"
            >
              <div className="mb-8 sm:mb-12">
                <h1 className="text-3xl sm:text-4xl font-bold text-foreground dark:text-white mb-3 sm:mb-4">
                  Welcome to RaiseBeacon
                </h1>
                <p className="text-base sm:text-lg text-muted-foreground px-2 sm:px-0">
                  Personal connections. Real opportunities.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <button
                  onClick={() => handleRoleSelect('founder')}
                  className="group relative p-6 sm:p-8 bg-card/80 dark:bg-slate-800/50 backdrop-blur rounded-2xl sm:rounded-3xl border border-border dark:border-slate-700 hover:border-[#2B7FFF] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] min-h-[180px] sm:min-h-0"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#2B7FFF]/20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-[#2B7FFF]/30 transition-colors">
                    <Briefcase className="w-7 h-7 sm:w-8 sm:h-8 text-[#2B7FFF]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground dark:text-white mb-2">
                    I'm a Founder
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Looking for the right investors for my startup
                  </p>
                  <ArrowRight className="w-5 h-5 text-[#2B7FFF] mx-auto mt-4 sm:mt-6 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
                </button>

                <button
                  onClick={() => handleRoleSelect('investor')}
                  className="group relative p-6 sm:p-8 bg-card/80 dark:bg-slate-800/50 backdrop-blur rounded-2xl sm:rounded-3xl border border-border dark:border-slate-700 hover:border-emerald-500 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] min-h-[180px] sm:min-h-0"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-emerald-500/20 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:bg-emerald-500/30 transition-colors">
                    <TrendingUp className="w-7 h-7 sm:w-8 sm:h-8 text-emerald-500" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground dark:text-white mb-2">
                    I'm an Investor
                  </h3>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Looking for promising startups to invest in
                  </p>
                  <ArrowRight className="w-5 h-5 text-emerald-500 mx-auto mt-4 sm:mt-6 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 'profile' && role === 'founder' && (
            <motion.div
              key="founder-profile"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-card/80 dark:bg-slate-800/50 backdrop-blur rounded-2xl sm:rounded-3xl border border-border dark:border-slate-700 p-5 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#2B7FFF]/20 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-[#2B7FFF]" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-foreground dark:text-white">Tell us about yourself</h2>
                  <p className="text-muted-foreground text-sm">This helps us find your perfect investor match</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Your Name</label>
                    <div className="relative">
                      <User className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        value={founderProfile.name}
                        onChange={(e) => setFounderProfile(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="John Smith"
                        className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Email</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        value={founderProfile.email}
                        onChange={(e) => setFounderProfile(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="john@company.com"
                        className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Company Name</label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      value={founderProfile.companyName}
                      onChange={(e) => setFounderProfile(prev => ({ ...prev, companyName: e.target.value }))}
                      placeholder="Acme Inc"
                      className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Website</label>
                  <div className="relative">
                    <Globe className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      value={founderProfile.website}
                      onChange={(e) => setFounderProfile(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://acme.com"
                      className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Location</label>
                  <Input
                    value={founderProfile.location}
                    onChange={(e) => setFounderProfile(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="San Francisco, CA"
                    className="bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                  />
                </div>

                <Button
                  onClick={() => setStep('details')}
                  disabled={!founderProfile.name || !founderProfile.email || !founderProfile.companyName}
                  className="w-full bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white py-5 sm:py-6 text-base sm:text-lg min-h-[56px] sm:min-h-[64px]"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'details' && role === 'founder' && (
            <motion.div
              key="founder-details"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-card/80 dark:bg-slate-800/50 backdrop-blur rounded-2xl sm:rounded-3xl border border-border dark:border-slate-700 p-5 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-[#2B7FFF]/20 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-[#2B7FFF]" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-foreground dark:text-white">What are you building?</h2>
                  <p className="text-muted-foreground text-sm">Help investors understand your vision</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Describe your business</label>
                  <textarea
                    value={founderProfile.description}
                    onChange={(e) => setFounderProfile(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="We're building..."
                    rows={4}
                    className="w-full p-3 bg-background dark:bg-slate-900/50 border border-border dark:border-slate-600 rounded-lg text-foreground dark:text-white placeholder:text-muted-foreground/70 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#2B7FFF]/50"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Current Stage</label>
                    <div className="grid grid-cols-2 gap-2">
                      {STAGES.map((stage) => (
                        <button
                          key={stage}
                          onClick={() => setFounderProfile(prev => ({ ...prev, stage }))}
                          className={cn(
                            "p-2 text-sm rounded-lg border transition-all",
                            founderProfile.stage === stage
                              ? "bg-[#2B7FFF]/20 border-[#2B7FFF] text-foreground dark:text-white"
                              : "bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-muted-foreground hover:border-foreground/40 dark:hover:border-slate-500"
                          )}
                        >
                          {stage}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Funding Needed</label>
                    <div className="space-y-2">
                      {['$50K-$250K', '$250K-$1M', '$1M-$5M', '$5M+'].map((amount) => (
                        <button
                          key={amount}
                          onClick={() => setFounderProfile(prev => ({ ...prev, fundingNeeded: amount }))}
                          className={cn(
                            "w-full p-2 text-sm rounded-lg border transition-all flex items-center gap-2",
                            founderProfile.fundingNeeded === amount
                              ? "bg-[#2B7FFF]/20 border-[#2B7FFF] text-foreground dark:text-white"
                              : "bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-muted-foreground hover:border-foreground/40 dark:hover:border-slate-500"
                          )}
                        >
                          <DollarSign className="w-3 h-3" />
                          {amount}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Industries (select all that apply)</label>
                  <div className="flex flex-wrap gap-2">
                    {INDUSTRIES.map((industry) => (
                      <button
                        key={industry}
                        onClick={() => toggleIndustry(industry, false)}
                        className={cn(
                          "px-3 py-1.5 text-sm rounded-full border transition-all",
                          founderProfile.industry.includes(industry)
                            ? "bg-[#2B7FFF]/20 border-[#2B7FFF] text-foreground dark:text-white"
                            : "bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-muted-foreground hover:border-foreground/40 dark:hover:border-slate-500"
                        )}
                      >
                        {industry}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep('profile')}
                    className="flex-1 border-border text-foreground hover:bg-muted dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleFounderSubmit}
                    disabled={!founderProfile.description || !founderProfile.stage || founderProfile.industry.length === 0}
                    className="flex-1 bg-[#2B7FFF] hover:bg-[#1A5FCC] text-white"
                  >
                    Complete Profile
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 'profile' && role === 'investor' && (
            <motion.div
              key="investor-profile"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-card/80 dark:bg-slate-800/50 backdrop-blur rounded-2xl sm:rounded-3xl border border-border dark:border-slate-700 p-5 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-foreground dark:text-white">Tell us about yourself</h2>
                  <p className="text-muted-foreground text-sm">This helps us find startups that match your criteria</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Your Name</label>
                    <div className="relative">
                      <User className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        value={investorProfile.name}
                        onChange={(e) => setInvestorProfile(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Jane Smith"
                        className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Email</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                      <Input
                        value={investorProfile.email}
                        onChange={(e) => setInvestorProfile(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="jane@firm.com"
                        className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Firm/Company Name</label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      value={investorProfile.firmName}
                      onChange={(e) => setInvestorProfile(prev => ({ ...prev, firmName: e.target.value }))}
                      placeholder="Smith Ventures"
                      className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Website</label>
                  <div className="relative">
                    <Globe className="w-4 h-4 text-muted-foreground/70 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      value={investorProfile.website}
                      onChange={(e) => setInvestorProfile(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://smithventures.com"
                      className="pl-10 bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Location</label>
                  <Input
                    value={investorProfile.location}
                    onChange={(e) => setInvestorProfile(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="New York, NY"
                    className="bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                  />
                </div>

                <Button
                  onClick={() => setStep('details')}
                  disabled={!investorProfile.name || !investorProfile.email || !investorProfile.firmName}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'details' && role === 'investor' && (
            <motion.div
              key="investor-details"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="bg-card/80 dark:bg-slate-800/50 backdrop-blur rounded-2xl sm:rounded-3xl border border-border dark:border-slate-700 p-5 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-foreground dark:text-white">Your Investment Focus</h2>
                  <p className="text-muted-foreground text-sm">Help founders understand what you look for</p>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Investor Type</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {INVESTOR_TYPES.map((type) => (
                      <button
                        key={type}
                        onClick={() => setInvestorProfile(prev => ({ ...prev, investorType: type }))}
                        className={cn(
                          "p-2 text-sm rounded-lg border transition-all",
                          investorProfile.investorType === type
                            ? "bg-emerald-500/20 border-emerald-500 text-foreground dark:text-white"
                            : "bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-muted-foreground hover:border-foreground/40 dark:hover:border-slate-500"
                        )}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Typical Check Size</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {CHECK_SIZES.map((size) => (
                      <button
                        key={size}
                        onClick={() => setInvestorProfile(prev => ({ ...prev, checkSize: size }))}
                        className={cn(
                          "p-2 text-sm rounded-lg border transition-all",
                          investorProfile.checkSize === size
                            ? "bg-emerald-500/20 border-emerald-500 text-foreground dark:text-white"
                            : "bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-muted-foreground hover:border-foreground/40 dark:hover:border-slate-500"
                        )}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Stages You Invest In</label>
                  <div className="flex flex-wrap gap-2">
                    {STAGES.map((stage) => (
                      <button
                        key={stage}
                        onClick={() => toggleStage(stage)}
                        className={cn(
                          "px-3 py-1.5 text-sm rounded-full border transition-all",
                          investorProfile.stages.includes(stage)
                            ? "bg-emerald-500/20 border-emerald-500 text-foreground dark:text-white"
                            : "bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-muted-foreground hover:border-foreground/40 dark:hover:border-slate-500"
                        )}
                      >
                        {stage}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/80 dark:text-slate-300 mb-2 block">Industries You Invest In</label>
                  <div className="flex flex-wrap gap-2">
                    {INDUSTRIES.map((industry) => (
                      <button
                        key={industry}
                        onClick={() => toggleIndustry(industry, true)}
                        className={cn(
                          "px-3 py-1.5 text-sm rounded-full border transition-all",
                          investorProfile.industries.includes(industry)
                            ? "bg-emerald-500/20 border-emerald-500 text-foreground dark:text-white"
                            : "bg-background dark:bg-slate-900/50 border-border dark:border-slate-600 text-muted-foreground hover:border-foreground/40 dark:hover:border-slate-500"
                        )}
                      >
                        {industry}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep('profile')}
                    className="flex-1 border-border text-foreground hover:bg-muted dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleInvestorSubmit}
                    disabled={!investorProfile.investorType || !investorProfile.checkSize || investorProfile.industries.length === 0}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Complete Profile
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {step === 'complete' && (
            <motion.div
              key="complete"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center bg-card/80 dark:bg-slate-800/50 backdrop-blur rounded-3xl border border-border dark:border-slate-700 p-12"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-[#2B7FFF] to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-foreground dark:text-white mb-4">
                You're all set!
              </h2>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {role === 'founder' 
                  ? "We've curated investors who are actively looking for startups like yours."
                  : "We've identified promising startups that match your investment criteria."}
              </p>
              
              {isAuthenticated ? (
                <Button
                  onClick={() => navigate('/search')}
                  className={cn(
                    "px-8 py-6 text-lg font-medium rounded-xl",
                    role === 'founder' 
                      ? "bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                      : "bg-emerald-500 hover:bg-emerald-600"
                  )}
                >
                  {role === 'founder' ? 'Find My Investors' : 'Discover Startups'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <div className="space-y-4">
                  <p className="text-foreground/80 dark:text-slate-300 text-sm">
                    Create your free account to view your personalized matches
                  </p>
                  <Button
                    onClick={() => setShowSignup(true)}
                    className={cn(
                      "px-8 py-6 text-lg font-medium rounded-xl",
                      role === 'founder' 
                        ? "bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                        : "bg-emerald-500 hover:bg-emerald-600"
                    )}
                  >
                    Create Free Account
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <p className="text-muted-foreground/70 text-xs">
                    Then choose a plan to unlock full access
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {/* Signup Modal for non-authenticated users */}
          <Dialog open={showSignup} onOpenChange={setShowSignup}>
            <DialogContent className="bg-card dark:bg-slate-800 border-border dark:border-slate-700 text-foreground dark:text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-xl">Create Your Account</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <p className="text-muted-foreground text-sm">
                  {role === 'founder'
                    ? "Save your investor preferences and unlock personalized matches."
                    : "Save your investment criteria and connect with promising startups."}
                </p>
                <div>
                  <label className="text-sm text-foreground/80 dark:text-slate-300 mb-1 block">Name</label>
                  <Input 
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    className="bg-background dark:bg-slate-900 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                  />
                </div>
                <div>
                  <label className="text-sm text-foreground/80 dark:text-slate-300 mb-1 block">Email</label>
                  <Input 
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="bg-background dark:bg-slate-900 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                  />
                </div>
                <div>
                  <label className="text-sm text-foreground/80 dark:text-slate-300 mb-1 block">Password</label>
                  <Input 
                    type="password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="bg-background dark:bg-slate-900 border-border dark:border-slate-600 text-foreground dark:text-white h-12"
                  />
                </div>
                <Button 
                  onClick={handleCreateAccount}
                  disabled={isCreatingAccount || !signupName || !signupEmail || !signupPassword}
                  className={cn(
                    "w-full py-5 sm:py-6 text-base sm:text-lg font-medium min-h-[56px] sm:min-h-[64px]",
                    role === 'founder' 
                      ? "bg-[#2B7FFF] hover:bg-[#1A5FCC]"
                      : "bg-emerald-500 hover:bg-emerald-600"
                  )}
                >
                  {isCreatingAccount ? 'Creating Account...' : 'Create Account & Continue'}
                </Button>
                <p className="text-muted-foreground/70 text-xs text-center">
                  By creating an account, you agree to our Terms and Privacy Policy
                </p>
              </div>
            </DialogContent>
          </Dialog>

          {/* Email Exists Modal */}
          <EmailExistsModal
            isOpen={showEmailExistsModal}
            onClose={() => {
              setShowEmailExistsModal(false);
              setExistingEmail('');
            }}
            email={existingEmail}
            redirectDelay={5}
          />
        </AnimatePresence>
      </div>
      </div>
    </>
  );
}

export default OnboardingPage;
// Force refresh
